/**
 * Cookie consent types for multi-jurisdictional privacy compliance
 * Supports GDPR (EU), CCPA/CPRA (US), PDPA (Asia)
 */

export type CookieCategory = 'essential' | 'analytics' | 'marketing';

export interface ConsentPreferences {
  essential: boolean; // Always true - required for site functionality
  analytics: boolean; // Microsoft Clarity, Google Analytics
  marketing: boolean; // Facebook Pixel, ad tracking
  timestamp: number; // When consent was given (Unix timestamp)
  version: string; // Consent policy version
  ipHash?: string; // Hashed IP for audit trail (optional)
}

export interface ConsentRecord {
  preferences: ConsentPreferences;
  userAgent: string; // Browser/device info for audit
  expiresAt: number; // Consent expiration (12 months from grant)
  lastUpdated: number; // Last time preferences were modified
}

export interface CookieInfo {
  name: string;
  category: CookieCategory;
  purpose: string;
  expiration: string;
  provider: string;
  isThirdParty: boolean;
}

export interface TrackingService {
  name: string;
  category: CookieCategory;
  description: string;
  dataCollected: string[];
  purpose: string;
  retention: string;
  provider: string;
  thirdPartySharing: boolean;
}

// Consent policy version - increment when privacy policy changes significantly
export const CONSENT_VERSION = '1.0.0';

// Consent expiry duration (12 months per GDPR recommendation)
export const CONSENT_EXPIRY_MONTHS = 12;

// LocalStorage key for consent preferences
export const CONSENT_STORAGE_KEY = 'emplo_consent_preferences';

// Tracking services configuration
export const TRACKING_SERVICES: TrackingService[] = [
  {
    name: 'Microsoft Clarity',
    category: 'analytics',
    description: 'Session recording and heatmap analytics to understand user behavior and improve site experience',
    dataCollected: ['Page views', 'Clicks', 'Scroll depth', 'Mouse movements', 'Anonymized IP'],
    purpose: 'Site optimization and bug detection',
    retention: '30 days',
    provider: 'Microsoft Corporation',
    thirdPartySharing: true
  },
  {
    name: 'Google Analytics',
    category: 'analytics',
    description: 'Web analytics service to track and report website traffic',
    dataCollected: ['Page views', 'Session duration', 'Bounce rate', 'Anonymized IP', 'Device info'],
    purpose: 'Understand visitor behavior and improve content',
    retention: '14 months',
    provider: 'Google LLC',
    thirdPartySharing: true
  },
  {
    name: 'Facebook Pixel',
    category: 'marketing',
    description: 'Tracks conversions from Facebook ads, optimizes ads, and builds targeted audiences',
    dataCollected: ['Page visits', 'Form submissions', 'Button clicks', 'Ad interactions', 'Facebook Click ID'],
    purpose: 'Ad targeting and conversion tracking',
    retention: '90 days',
    provider: 'Meta Platforms Inc.',
    thirdPartySharing: true
  }
];

// Cookie information for Cookie Policy page
export const COOKIE_LIST: CookieInfo[] = [
  // Essential cookies
  {
    name: 'accessToken, refreshToken',
    category: 'essential',
    purpose: 'Authentication and secure login (stored in localStorage)',
    expiration: 'Session',
    provider: 'EmploSkills',
    isThirdParty: false
  },
  {
    name: 'emplo_consent_preferences',
    category: 'essential',
    purpose: 'Stores your cookie consent preferences (localStorage)',
    expiration: '12 months',
    provider: 'EmploSkills',
    isThirdParty: false
  },
  // Analytics cookies
  {
    name: '_clck, _clsk',
    category: 'analytics',
    purpose: 'Microsoft Clarity analytics cookies',
    expiration: '1 year',
    provider: 'Microsoft Clarity',
    isThirdParty: true
  },
  {
    name: '_ga, _gid, _gat',
    category: 'analytics',
    purpose: 'Google Analytics tracking cookies',
    expiration: '2 years (_ga), 24 hours (_gid)',
    provider: 'Google Analytics',
    isThirdParty: true
  },
  // Marketing cookies
  {
    name: '_fbp',
    category: 'marketing',
    purpose: 'Facebook Pixel tracking cookie',
    expiration: '90 days',
    provider: 'Facebook',
    isThirdParty: true
  },
  {
    name: '_fbc',
    category: 'marketing',
    purpose: 'Stores Facebook Click ID for ad attribution',
    expiration: '90 days',
    provider: 'Facebook',
    isThirdParty: true
  },
  {
    name: 'hubspotutk',
    category: 'marketing',
    purpose: 'HubSpot form tracking cookie',
    expiration: '13 months',
    provider: 'HubSpot',
    isThirdParty: true
  }
];
